import React, { useEffect, useState } from "react";
import flat from "flat";
import { Stepper } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useLocation } from "react-router-dom";
import { subDays, addDays, parseISO } from "date-fns";
import AgreementForm1 from "./AgreementForm1";
import {
  createAgreement,
  getAgreementDetails,
  updateAgreement,
} from "../../utilities/apis/agreements";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { loadingStates } from "../../utilities/utilities";
import AgreementForm2 from "./AgreementForm2";
import AgreementForm3 from "./AgreementForm3";
import AgreementForm4 from "./AgreementForm4";
import colors, { themeColor } from "../../utilities/design";
import AgreementForm5 from "./AgreementForm5";
import { useSearchParams } from "react-router-dom";
import { addMonths, addYears, differenceInDays } from "date-fns";
import { getCookiesSession } from "../../cookiesSession";

const AgreementCreatePage = () => {
  let FORM_STEPS;
  const [searchParams] = useSearchParams();
  const ag = searchParams.get("ag");
  let userData = getCookiesSession("trueCounselUserDataMaster");
  userData = userData ? JSON.parse(userData).user : null;
  const isRequestingUserMissing = (requestingUser) =>
    !requestingUser?.name ||
    !String(requestingUser.name).trim() ||
    !requestingUser?.email ||
    !String(requestingUser.email).trim();
  const requestingUserFromLogin = () => ({
    name: userData?.name || "",
    email: userData?.email || "",
  });
  const [flowData, setFlowData] = useState({
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
    sendfordirectsign: false,
  });

  const initialState = {
    typeOfAgreementData: [],
    typeOfAgreement: null,
    titleOfAgreementData: [],
    titleOfAgreement: null,
    relationshipType: "",
    relationshipWith: ag ? ag : "",
    requestingUser: {
      name: "",
      email: "",
      phone: "",
      designation: "",
      businessDepartment: "",
    },
    dateOfContract: null,
    noOfMonths: null,
    noOfYears: "",
    reminderTimeInDays: 15,
    terminationNoticePeriod: null,
    agreementValue: 0,
    contractQuillJsDelta: {
      ops: [{ insert: "Agreement content goes here..." }],
    },
    firstParty: {},
    secondParty: {},
    thirdParty: {},
    fourthParty: {},
    fifthParty: {},
    sixthParty: {},
    seventhParty: {},
    eighthParty: {},
    ninthParty: {},
    tenthParty: {},
    eleventhParty: {},
    twelthParty: {},
    thirteenthParty: {},
    fourteenthParty: {},
    fifteenthParty: {},
    sixteenthParty: {},
    seventeenthParty: {},
    eighteenthParty: {},
    nineteenthParty: {},
    twentythParty: {},
    documents: [],
    formCompleted: false,
    location: null,
    businessUnit: "1",
  };
  const { state } = useLocation();
  const [agreementDetails, setAgreementDetails] = useState({
    agreementLoaded: false,
    agreementId: state?.agreementId || null,
  });
  const [actionText, SetActionText] = useState("draft");
  const [agreementStatus, setAgreementStatus] = useState(null);
  FORM_STEPS = {
    STEP_1: 0,
    STEP_2: 1,
    STEP_3: 2,
    STEP_4: 3,
    STEP_5: 4,
  };
  const [uiConfigs, setUiConfigs] = useState({
    currentStep: FORM_STEPS.STEP_1,
    loading: loadingStates.NO_ACTIVE_REQUEST,
  });
  const agreementForm = useForm({ initialValues: flat(initialState) });
  const saveAgreement = async (values) => {
    const incoming = values || agreementForm.values;
    const nestedTemplateFields = incoming.templateFields;
    const restValues = { ...incoming };
    delete restValues.templateFields;

    const formValues = {
      ...flat.unflatten(restValues),
      ...(nestedTemplateFields ? { templateFields: nestedTemplateFields } : {}),
    };

    const agreementData = {
      json: formValues,
      flatted: incoming,
    };

    const toUse = agreementDetails.agreementId
      ? updateAgreement
      : createAgreement;

    let endDate = null;
    let totalDays = null;
    let contractDueDate = null;

    if (formValues.dateOfContract) {
      const startDate = parseISO(formValues.dateOfContract);

      endDate = formValues.dueDateOfContract
        ? parseISO(formValues.dueDateOfContract)
        : subDays(
            addDays(
              addMonths(
                addYears(startDate, parseInt(formValues.noOfYears || 0, 10)),
                parseInt(formValues.noOfMonths || 0, 10),
              ),
              parseInt(formValues.noOfDays || 0, 10),
            ),
            1,
          );

      totalDays = differenceInDays(endDate, startDate);
      contractDueDate = addDays(startDate, totalDays);
    }

    const isExisting = Boolean(agreementDetails.agreementId);
    const current = String(
      (isExisting && agreementStatus) || actionText || "draft",
    );
    const promote =
      formValues.formCompleted && current.toLowerCase() === "draft";

    const agreementArgs = {
      typeOfAgreement: formValues.typeOfAgreement,
      titleOfAgreement: formValues.titleOfAgreement,
      requestingUserDetails: formValues.requestingUser,
      dateOfContract: formValues.dateOfContract,
      contractTermInDays: totalDays,
      noOfYears: parseInt(formValues.noOfYears, 10),
      noOfMonths: parseInt(formValues.noOfMonths, 10),
      noOfMonths: parseInt(formValues.noOfMonths, 10),
      agreementValue: parseInt(formValues.agreementValue, 10),
      location: formValues.location,
      reminderTimeInDays: parseInt(formValues.reminderTimeInDays, 10),
      terminationNoticePeriod: formValues.terminationNoticePeriod
        ? parseInt(formValues.terminationNoticePeriod, 10)
        : null,
      dueDateOfContract: endDate,
      reminderTime:
        contractDueDate && formValues.reminderTimeInDays
          ? subDays(contractDueDate, formValues.reminderTimeInDays)
          : null,
      contractQuillJsDelta: formValues.contractQuillJsDelta,
      firstParty: formValues.firstParty,
      secondParty: formValues.secondParty,
      thirdParty: formValues.thirdParty,
      sixthParty: formValues.sixthParty,
      seventhParty: formValues.seventhParty,
      eighthParty: formValues.eighthParty,
      ninthParty: formValues.ninthParty,
      tenthParty: formValues.tenthParty,
      eleventhParty: formValues.eleventhParty,
      twelthParty: formValues.twelthParty,
      thirteenthParty: formValues.thirteenthParty,
      fourteenthParty: formValues.fourteenthParty,
      fifteenthParty: formValues.fifteenthParty,
      sixteenthParty: formValues.sixteenthParty,
      seventeenthParty: formValues.seventeenthParty,
      eighteenthParty: formValues.eighteenthParty,
      nineteenthParty: formValues.nineteenthParty,
      twentythParty: formValues.twentythParty,
      fourthParty: formValues.fourthParty,
      fifthParty: formValues.fifthParty,
      formCompleted: !!formValues.formCompleted,
      relationshipType: formValues.relationshipType,
      relationshipWith: formValues.relationshipWith,
      businessUnit: formValues.businessUnit || "1",
      ...((!isExisting || promote) && {
        status: promote ? "CREATED" : current,
      }),
      ...(formValues.templateFields
        ? { templateFields: formValues.templateFields }
        : {}),
    };

    const args = agreementDetails.agreementId
      ? {
          agreementId: agreementDetails.agreementId,
          agreementData,
          ...agreementArgs,
        }
      : { agreementData, ...agreementArgs };
    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });
    const response = await apiWrapWithErrorWithData(toUse(args));
    if (response?.success) {
      if (agreementArgs.status) {
        SetActionText(agreementArgs.status);
        setAgreementStatus(agreementArgs.status);
      }

      if (response?.agreement?.id) {
        setAgreementDetails({
          ...agreementDetails,
          agreementId: response.agreement.id,
          agreementLoaded: true,
        });
      }
    } else {
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
      return false;
    }
    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.NO_ACTIVE_REQUEST,
    });
    return true;
  };

  const fetchAgreementData = async (agreementId) => {
    setUiConfigs({
      ...uiConfigs,
      loading: loadingStates.LOADING,
    });

    const convertResponseToFormValues = (responseAgreement) => ({
      typeOfAgreement: responseAgreement.typeOfAgreement,
      titleOfAgreement: responseAgreement.titleOfAgreement,
      requestingUser: isRequestingUserMissing(
        responseAgreement.requestingUserDetails,
      )
        ? requestingUserFromLogin()
        : responseAgreement.requestingUserDetails,
      dateOfContract: responseAgreement.dateOfContract || null,
      location: responseAgreement.location || null,
      agreementValue: responseAgreement.agreementValue,
      termOfContractInDays: responseAgreement.contractTermInDays || null,
      reminderTime: responseAgreement.reminderTime || null,
      contractQuillJsDelta: responseAgreement.contractQuillJsDelta || {
        ops: [{ insert: "Agreement content goes here..." }],
      },
      firstParty: responseAgreement.firstParty || {},
      secondParty: responseAgreement.secondParty || {},
      thirdParty: responseAgreement.thirdParty || {},
      fourthParty: responseAgreement.fourthParty || {},
      fifthParty: responseAgreement.fifthParty || {},
      sixthParty: responseAgreement.sixthParty || {},
      seventhParty: responseAgreement.seventhParty || {},
      eighthParty: responseAgreement.eighthParty || {},
      ninthParty: responseAgreement.ninthParty || {},
      tenthParty: responseAgreement.tenthParty || {},
      eleventhParty: responseAgreement.eleventhParty || {},
      twelthParty: responseAgreement.twelthParty || {},
      thirteenthParty: responseAgreement.thirteenthParty || {},
      fourteenthParty: responseAgreement.fourteenthParty || {},
      fifteenthParty: responseAgreement.fifteenthParty || {},
      sixteenthParty: responseAgreement.sixteenthParty || {},
      seventeenthParty: responseAgreement.seventeenthParty || {},
      eighteenthParty: responseAgreement.eighteenthParty || {},
      nineteenthParty: responseAgreement.nineteenthParty || {},
      twentythParty: responseAgreement.twentythParty || {},
      formCompleted: !!responseAgreement.formCompleted,
      noOfMonths: responseAgreement.agreementData?.json?.noOfMonths || null,
      noOfYears: responseAgreement.agreementData?.json?.noOfYears || null,
      // reminderTime: responseAgreement.agreementData.json.reminderTime,
      reminderTimeInDays: responseAgreement.reminderTimeInDays || 15,
      terminationNoticePeriod:
        responseAgreement.terminationNoticePeriod ?? null,
      relationshipType: responseAgreement.relationshipType,
      relationshipWith: responseAgreement.relationshipWith,
      businessUnit: responseAgreement.bussinessUnit || "1",
      templateFields: responseAgreement.templateFields || null,
    });

    const response = await apiWrapWithErrorWithData(
      getAgreementDetails({ agreementId }),
    );
    if (response?.success && response.agreement) {
      const loadedStatus = String(
        response.agreement.status ||
          response.agreement.actionText ||
          "draft",
      );
      SetActionText(loadedStatus);
      setAgreementStatus(response.agreement.status || loadedStatus);
      agreementForm.setValues((prevValues) =>
        flat({
          ...initialState,
          ...flat.unflatten(prevValues),
          ...convertResponseToFormValues(response.agreement),
        }),
      );
      setAgreementDetails({
        ...agreementDetails,
        agreementLoaded: true,
      });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    } else {
      // showNotification({
      //   color: 'red',
      //   title: 'Agreement',
      //   message: 'Something went wrong.',
      // });
      setUiConfigs({
        ...uiConfigs,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      });
    }
  };

  useEffect(() => {
    if (agreementDetails.agreementId && !agreementDetails.agreementLoaded) {
      fetchAgreementData(agreementDetails.agreementId);
    }

    if (userData) {
      const currentRequestingUser =
        flat.unflatten(agreementForm.values)?.requestingUser;
      if (isRequestingUserMissing(currentRequestingUser)) {
        agreementForm.setValues((prevValues) =>
          flat({
            ...initialState,
            ...flat.unflatten(prevValues),
            requestingUser: requestingUserFromLogin(),
          }),
        );
      }
    }
  }, [agreementDetails.agreementId, agreementDetails.agreementLoaded]);

  const allowStepSelect =
    !!agreementForm.values.formCompleted || !!agreementDetails.agreementId;

  return (
    <Stepper
      color={themeColor(colors.agreement)}
      active={uiConfigs.currentStep}
      onStepClick={(step) => {
        setUiConfigs({
          ...uiConfigs,
          currentStep: step,
        });
      }}
      breakpoint="sm"
      style={{ paddingRight: "10px" }}
      // iconSize={{window.innerWidth} < 850 ? 1 : 30}
      // size="20"
    >
      <Stepper.Step allowStepSelect={allowStepSelect} label="Key Details">
        <AgreementForm1
          id={agreementDetails.agreementId}
          save={saveAgreement}
          uiConfigs={uiConfigs}
          setUiConfigs={setUiConfigs}
          form={agreementForm}
        />
      </Stepper.Step>
      <Stepper.Step allowStepSelect={allowStepSelect} label="Terms">
        <AgreementForm2
          id={agreementDetails.agreementId}
          save={saveAgreement}
          uiConfigs={uiConfigs}
          setUiConfigs={setUiConfigs}
          form={agreementForm}
          initialState={initialState}
          edit={state?.edit ?? false}
        />
      </Stepper.Step>
      <Stepper.Step allowStepSelect={allowStepSelect} label="Parties Details">
        <AgreementForm3
          flowData={flowData}
          setFlowData={setFlowData}
          actionText={actionText}
          id={agreementDetails.agreementId}
          save={saveAgreement}
          uiConfigs={uiConfigs}
          setUiConfigs={setUiConfigs}
          form={agreementForm}
          initialState={initialState}
          edit={state?.edit ?? false}
        />
      </Stepper.Step>
      <Stepper.Step allowStepSelect={allowStepSelect} label="User Roles">
        <AgreementForm5
          flowData={flowData}
          setFlowData={setFlowData}
          id={agreementDetails.agreementId}
          save={saveAgreement}
          uiConfigs={uiConfigs}
          setUiConfigs={setUiConfigs}
          form={agreementForm}
          initialState={initialState}
        />
      </Stepper.Step>
      <Stepper.Step allowStepSelect={allowStepSelect} label="Content">
        <AgreementForm4
          id={agreementDetails.agreementId}
          save={saveAgreement}
          uiConfigs={uiConfigs}
          setUiConfigs={setUiConfigs}
          form={agreementForm}
        />
      </Stepper.Step>
    </Stepper>
  );
};

export default AgreementCreatePage;
